import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { EventsService } from "../../../_services/eventsServices/events.service";

import { FormGroup } from "@angular/forms";
import { parentEventQuestionsCardSelectMenuOptions } from "./parent-event-questions-card-select-menu-options";
import { SelectMenuOption } from "../../../form/select-menu/select-menu.component";
import {
  ParentQuestion,
  ParentQuestionType,
} from "../event-page-models/parent-question";
import { QuestionCartOptionEvent } from "./question-card-option/questions-card-option.component";
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import { Subscription } from "rxjs/Subscription";
import { interval } from "rxjs/observable/interval";
import { takeUntil } from "rxjs/operators";
import { Subject } from "rxjs/Subject";
import { EventSerializerService } from "../../../_services/eventsServices/eventSerializer.service";

export type CardButtonName = "delete" | "moveUp" | "moveDown" | "copy" | "save";

@Component({
  selector: "parent-event-page-form-parent-question-card",
  templateUrl:
    "./create-parent-event-form-parent-questions-card.component.html",
  styleUrls: [
    "./create-parent-event-form-parent-questions-card.component.scss",
  ],
  animations: [
    trigger("openClose", [
      state(
        "open",
        style({
          maxHeight: "3000px",
          opacity: 1,
        })
      ),
      state(
        "closed",
        style({
          display: "none",
          maxHeight: "1px",
          height: "1px",
          opacity: 0,
        })
      ),
      transition("open => closed", [animate("0.2s")]),
      transition("* => open", [animate("0.2s")]),
      transition("closed => open", [animate("0.2s")]),
    ]),
  ],
})
export class CreateParentEventFormParentQuestionsCardComponent
  implements OnInit, OnDestroy
{
  public readonly defaultOption = {
    label: "",
    value: "",
    order: 0,
    idKey: 0,
  };
  public parentEventDetailsFormGroup: FormGroup | null = null;
  public readonly sideButtons: CardButtonName[] = [
    "moveUp",
    "moveDown",
    "copy",
    "delete",
    // "save",
  ];
  public title: string;
  public readonly selectMenuOptions = parentEventQuestionsCardSelectMenuOptions;
  public questionType: SelectMenuOption = this.selectMenuOptions[1];
  public options: Array<{
    label: string;
    value: string | boolean;
    order: number;
    idKey: number;
    id: string;
  }>;
  public parentQuestion: ParentQuestion = {
    answer: "",
    isRequired: false,
    options: [],
    order: 0,
    questionsType: this.questionType.customValue as ParentQuestionType,
    subTitle: "",
    title: "",
    id: EventSerializerService.createNewQuestionId(),
  };
  @Output("cardFocusEvent")
  public cardFocusEvent: EventEmitter<ParentQuestion> = new EventEmitter<ParentQuestion>();
  @Input("isActive") public isActive: boolean = false;
  public isDirty: boolean = false;
  private intervals$: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(private eventsService: EventsService) {
    this.buildEmptyOptionsOrRemoveOptionsIfNeeded(
      this.parentQuestion.questionsType
    );
  }

  @Input("setParentQuestion") set setParentQuestions(val: ParentQuestion) {
    this.parentQuestion = val;
    const questionType = this.selectMenuOptions.find(
      (qt) => qt.customValue === val.questionsType
    );
    this.questionType = questionType || this.questionType;
    this.options = (this.parentQuestion.options || []).map((o, i) => ({
      ...o,
      idKey: i,
    }));
  }

  public getSideButtons(type: "location" | "edit"): CardButtonName[] {
    return this.sideButtons.filter((b) =>
      type === "location" ? b.includes("move") : !b.includes("move")
    );
  }

  public ngOnInit(): void {
    interval(4000)
      .pipe(takeUntil(this.destroy$))
      .subscribe((int) => {
        if (this.isDirty) {
          this.eventsService.updateParentQuestion(this.parentQuestion, {
            updateState: true,
          });
          this.isDirty = false;
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public setQuestionType($event: SelectMenuOption) {
    if (this.questionType === $event) {
      return;
    }
    this.isDirty = true;
    this.questionType = $event;
    this.parentQuestion.questionsType = $event.customValue;
    if (
      this.parentQuestion.questionsType === ParentQuestionType.Dropdown ||
      this.parentQuestion.questionsType === ParentQuestionType.CheckBox
    ) {
      this.buildEmptyOptionsOrRemoveOptionsIfNeeded(
        this.parentQuestion.questionsType
      );
    }
    if (this.parentQuestion.questionsType === ParentQuestionType.Dropdown) {
      this.addEmptyOptionIfNeeded();
    }
    this.cardFocusEvent.emit(this.parentQuestion);
  }

  public isDropdown() {
    return this.parentQuestion.questionsType === ParentQuestionType.Dropdown;
  }
  public isCheckBox() {
    return this.parentQuestion.questionsType === ParentQuestionType.CheckBox;
  }

  public addEmptyOptionIfNeeded() {
    const questionType = this.parentQuestion.questionsType;
    const length = this.options.length;
    let idKey = 1;
    if (!length) {
      this.options.push({
        ...this.defaultOption,
        idKey,
        id: EventSerializerService.createNewQuestionId(),
      });
      return;
    }
    if (questionType === ParentQuestionType.CheckBox) {
      return;
    }
    const lastOption = this.options[length - 1];

    idKey = Math.max(...this.options.map((o) => o.idKey)) + 1;

    if (lastOption.label) {
      this.options.push({
        ...this.defaultOption,
        idKey,
        id: EventSerializerService.createNewQuestionId(),
      });
    } else if (length > 1 && !lastOption.label) {
      if (!this.options[length - 2].label) {
        this.options.pop();
      }
    }
  }

  public optionEventHandler($event: QuestionCartOptionEvent) {
    const thisOption = this.options.find((o) => o.idKey === $event.id);
    if (!thisOption) {
      return;
    }
    this.isDirty = true;

    switch ($event.eventType) {
      case "changedValue":
        thisOption.label = $event.label;
        this.parentQuestion.options = this.options;
        this.addEmptyOptionIfNeeded();
        break;
      case "remove":
        this.options = this.options.filter((o) => o.idKey !== $event.id);
        this.parentQuestion.options = this.options;
        this.addEmptyOptionIfNeeded();
        break;
      case "moveUp":
        const indexOfQuestion = this.options.indexOf(thisOption);
        this.arrayMove(this.options, indexOfQuestion, indexOfQuestion - 1);
        this.parentQuestion.options = this.options;
        this.addEmptyOptionIfNeeded();
        break;
      case "moveDown":
        break;
    }
  }

  public buttonIcon(button: CardButtonName) {
    const dict = {
      save: "fa-save",
      delete: "fa-trash",
      moveUp: "fa-angle-up",
      moveDown: "fa-angle-down",
      copy: "fa-copy",
    };
    return dict[button];
  }

  public cardButtonAction($event: Event, button: CardButtonName) {
    switch (button) {
      case "delete":
        this.eventsService.updateParentQuestion(this.parentQuestion, {
          delete: true,
        });
        break;
      case "moveUp":
        $event.stopPropagation();
        $event.stopImmediatePropagation();
        this.eventsService.updateParentQuestion(this.parentQuestion, {
          move: -1,
        });
        break;
      case "moveDown":
        $event.stopPropagation();
        $event.stopImmediatePropagation();
        this.eventsService.updateParentQuestion(this.parentQuestion, {
          move: 1,
        });
        break;
      case "save":
        this.isDirty = false;
        this.eventsService.updateParentQuestion(this.parentQuestion);
        this.isDirty = false;
        break;
      case "copy":
        this.eventsService.updateParentQuestion(this.parentQuestion, {
          duplicate: true,
        });
    }
  }

  public clickedCard($event: MouseEvent) {
    this.cardFocusEvent.emit(this.parentQuestion);
  }

  public editedText($event: any) {
    this.isDirty = true;
  }

  private arrayMove(arr, fromIndex, toIndex) {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
  }

  private buildEmptyOptionsOrRemoveOptionsIfNeeded(
    questionType: ParentQuestionType
  ) {
    if (
      questionType === ParentQuestionType.CheckBox &&
      this.options &&
      this.options.length > 1
    ) {
      this.options = [this.options[0]];
      return;
    }
    if (this.options && this.options.length) {
      return;
    }
    this.options = [1].map((id) => ({
      ...this.defaultOption,
      idKey: id,
      id: EventSerializerService.createNewQuestionId(),
    }));
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/events-screen/create-parent-event-form-parent-questions-card/create-parent-event-form-parent-questions-card.component.ts